import { useEffect, useRef, type ReactNode } from "react";
import { cn } from "@/lib/utils/cn";

export function ChatActivityIndicator({
  active,
  children,
  className,
  appearance = "dot",
}: {
  active: boolean;
  children?: ReactNode;
  className?: string;
  appearance?: "dot" | "spinner";
}) {
  const indicator = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const element = indicator.current;
    if (!element) return;
    let inView = true;
    const update = () => {
      element.style.animationPlayState =
        active && inView && document.visibilityState !== "hidden"
          ? "running"
          : "paused";
    };
    const observer =
      typeof IntersectionObserver === "undefined"
        ? undefined
        : new IntersectionObserver(([entry]) => {
            inView = entry.isIntersecting;
            update();
          });
    observer?.observe(element);
    document.addEventListener("visibilitychange", update);
    update();
    return () => {
      observer?.disconnect();
      document.removeEventListener("visibilitychange", update);
    };
  }, [active]);
  return (
    <span
      aria-hidden="true"
      className={cn(
        "relative inline-flex size-3.5 shrink-0 items-center justify-center",
        className,
      )}
    >
      <span
        ref={indicator}
        data-chat-activity-indicator
        className={cn(
          "rounded-full transition-opacity [transition-duration:300ms] motion-reduce:animate-none motion-reduce:transition-none",
          appearance === "spinner"
            ? "size-3 border-[1.5px] border-current border-r-transparent [animation-duration:2.4s] [animation-timing-function:linear]"
            : "size-1.5 bg-current [animation-duration:3s] [animation-timing-function:ease-in-out]",
          active
            ? appearance === "spinner"
              ? "animate-spin opacity-100"
              : "animate-pulse opacity-100"
            : "opacity-0",
        )}
      />
      <span
        className={cn(
          "absolute inset-0 flex items-center justify-center transition-opacity duration-300 motion-reduce:transition-none",
          active ? "opacity-0" : "opacity-100",
        )}
      >
        {children}
      </span>
    </span>
  );
}
